import { useState } from "react";
import { BookOpen, ExternalLink, X } from "lucide-react";

const DISMISS_KEY = "specstory-analytics-edu-dismissed";
const VIDEO_HREF = "https://www.youtube.com/watch?v=3fDeT86sDb8";
const DOCS_HREF = "https://docs.specstory.com/cloud/analytics";
const THUMB = "/assets/analytics-video-thumbnail.png";

/**
 * The education content itself: a heading + two cards (video + docs). Shared by
 * the inline dismissable section (unentitled viewers) and the help dialog
 * (entitled viewers). No wrapper or dismiss button — callers frame it.
 */
export function AnalyticsEducationContent() {
  return (
    <div>
      <div className="min-w-0">
        <h3 className="text-sm font-semibold text-foreground">
          How analytics works
        </h3>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          See spend, activity, and prompt quality across every coding agent
          you use. Watch the walkthrough or read the docs.
        </p>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {/* Video card */}
        <a
          href={VIDEO_HREF}
          target="_blank"
          rel="noopener noreferrer"
          className="group block relative rounded-md border border-border overflow-hidden focus:outline-none focus:ring-2 focus:ring-brand/40"
          aria-label="Watch the analytics walkthrough on YouTube"
        >
          <img
            src={THUMB}
            alt="Analytics walkthrough"
            className="w-full h-40 sm:h-44 object-cover object-center bg-muted"
            loading="lazy"
          />
          <span
            aria-hidden
            className="absolute inset-0 flex items-center justify-center bg-black/30 transition-colors group-hover:bg-black/40"
          >
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/90 text-black shadow-lg transition-transform group-hover:scale-105">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
          </span>
          <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-md bg-black/70 px-2 py-1 text-[11px] font-medium text-white">
            <ExternalLink size={12} />
            YouTube
          </span>
          <span className="block px-3 py-2 text-xs font-medium text-foreground">
            Watch the walkthrough
          </span>
        </a>

        {/* Docs card */}
        <a
          href={DOCS_HREF}
          target="_blank"
          rel="noopener noreferrer"
          className="group flex flex-col justify-between rounded-md border border-border bg-muted/30 p-4 hover:bg-muted/60 focus:outline-none focus:ring-2 focus:ring-brand/40"
        >
          <div className="flex items-start gap-3">
            <span className="flex-shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-md bg-brand/10 text-brand">
              <BookOpen className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">
                Read the docs
              </p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                Spend, activity, and prompt-quality metrics across all your
                coding agents, and how to read them.
              </p>
            </div>
          </div>
          <span className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-brand">
            <ExternalLink size={12} />
            docs.specstory.com
          </span>
        </a>
      </div>
    </div>
  );
}

/**
 * Dismissable inline education section — shown on the Analytics gate page for
 * unentitled viewers. Hidden once dismissed (persisted in localStorage).
 */
export function AnalyticsEducation() {
  const [dismissed, setDismissed] = useState<boolean>(
    () =>
      typeof window !== "undefined" &&
      window.localStorage.getItem(DISMISS_KEY) === "1",
  );

  if (dismissed) return null;

  const dismiss = () => {
    window.localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <section className="mt-6 rounded-lg border border-border bg-card overflow-hidden">
      <div className="flex items-start justify-between gap-3 px-4 pt-4">
        <AnalyticsEducationContent />
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="flex-shrink-0 -mt-1 -mr-1 inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
        >
          <X size={15} />
        </button>
      </div>
      <div className="h-4" />
    </section>
  );
}
